import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetClientRoomState } from '@/shared/stores/reset-room';

const SLICE_COUNT = 5;
const DURATION = 500;
const TICK = 35;
const MAX_OFFSET = 80;

const DISSOLVE_REASON_LABEL: Record<string, string> = {
  host_closed: 'The owner dismissed the room',
  idle_timeout: 'The room has been inactive for a long time and has been automatically dismissed.',
  empty: 'All players have left and the room has been dismissed',
};

export default function CheatOverlay({ dissolvedReason }: { dissolvedReason?: string | null }) {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLDivElement>(null);
  const realRef = useRef<HTMLDivElement>(null);
  const [showBtn, setShowBtn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const real = realRef.current;
    if (!canvas || !real) return;

    const cuts = [0];
    for (let i = 1; i < SLICE_COUNT; i++) {
      cuts.push(Math.round((i / SLICE_COUNT) * 100 + (Math.random() * 8 - 4)));
    }
    cuts.push(100);

    const slices: HTMLDivElement[] = [];
    for (let i = 0; i < SLICE_COUNT; i++) {
      const el = document.createElement('div');
      el.className = 'cheat-gs';
      el.innerHTML = real.innerHTML;
      const tl = cuts[i]!;
      const tr = cuts[i]! + Math.round(Math.random() * 6 - 3);
      const br = cuts[i + 1]! + Math.round(Math.random() * 6 - 3);
      const bl = cuts[i + 1]!;
      el.style.clipPath = `polygon(0 ${tl}%, 100% ${tr}%, 100% ${br}%, 0 ${bl}%)`;
      canvas.appendChild(el);
      slices.push(el);
    }

    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += TICK;
      const progress = elapsed / DURATION;
      if (progress >= 1) {
        clearInterval(interval);
        canvas.style.visibility = 'hidden';
        setShowBtn(true);
        return;
      }
      const strength = Math.pow(1 - progress, 2.5);
      for (const el of slices) {
        const roll = Math.random();
        let offset: number;
        if (roll < 0.25) offset = 0;
        else if (roll < 0.5) offset = (Math.random() - 0.5) * MAX_OFFSET * 0.3 * strength;
        else offset = (Math.random() - 0.5) * MAX_OFFSET * 2 * strength;
        el.style.transform = `translateX(${Math.round(offset)}px)`;
      }
    }, TICK);

    return () => clearInterval(interval);
  }, []);

  const handleContinue = () => {
    resetClientRoomState();
    navigate('/');
  };

  return (
    <div className="cheat-overlay">
      <div className="cheat-real" ref={realRef}>
        <div className="cheat-title" data-text="Cheater detected">Cheater detected</div>
        <div className="cheat-subtitle">Game terminated</div>
        <div className="cheat-desc">The cheater has been punished, the game is canceled and all players' wins and losses are not counted.</div>
        <div className="cheat-divider" />
        {showBtn && (
          <button className="cheat-btn cheat-btn-visible" onClick={handleContinue}>continue</button>
        )}
      </div>
      <div className="cheat-glitch-canvas" ref={canvasRef} />
      <div className="cheat-flash" />
      {dissolvedReason && (
        <div className="fixed bottom-6 right-8 text-xs text-white/30 select-text pointer-events-auto">
          {DISSOLVE_REASON_LABEL[dissolvedReason] ?? dissolvedReason}
        </div>
      )}
    </div>
  );
}
